import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { InputComponent } from 'src/app/@core/components/ui/input/input.component';
import { PromptEnhancementData, ThreadMessagesService } from '../../../services/thread-messages.service';
import {
    AUTO_APPROVE_SECONDS,
    clearCountdown,
    formatCountdown,
    readCountdown,
    writeCountdown,
} from '../../../utils/interrupt-countdown';

/**
 * PromptEnhancementCardComponent
 *
 * Displays the Markdown "enhanced prompt" produced by the PromptEnhancerMiddleware's
 * ``enhance_prompt`` tool before the agent begins work.  The document is written in
 * the user's voice (no technical paths/IDs) so the user can:
 *  - Read the enhanced prompt as-is and approve it.
 *  - Edit the Markdown directly and approve the edited version.
 *  - Ask the AI to revise the enhanced prompt via a freeform feedback textarea.
 *
 * Outputs:
 *  - ``approve``          — user accepted the enhanced prompt as-is.
 *  - ``editApprove``      — user edited the Markdown and wants to proceed.
 *  - ``dismiss``          — user rejected the enhancement (kept for parent compatibility).
 *  - ``reviseRequested``  — user submitted feedback for AI-driven revision.
 */
@Component({
    selector: 'app-prompt-enhancement-card',
    standalone: true,
    imports: [CommonModule, FormsModule, TooltipModule, ButtonComponent, IconComponent, InputComponent],
    templateUrl: './prompt-enhancement-card.component.html',
    styleUrl: './prompt-enhancement-card.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptEnhancementCardComponent implements OnInit, OnDestroy {
    // ── Inputs ───────────────────────────────────────────────────────────────
    /** Full prompt-enhancement payload emitted by the middleware. */
    plan = input.required<PromptEnhancementData>();

    // ── Outputs ──────────────────────────────────────────────────────────────
    /** User approved without edits. */
    approve = output<void>();

    /** User edited the Markdown and wants to proceed. */
    editApprove = output<string>();

    /** User rejected / dismissed the enhanced prompt. */
    dismiss = output<void>();

    /** User submitted freeform feedback for AI to regenerate the enhanced prompt. */
    reviseRequested = output<string>();

    /** User wants to save the current prompt to the prompt library. */
    savePrompt = output<string>();

    /** When true the countdown is frozen (e.g. Save Prompt dialog is open). */
    pauseTimer = input<boolean>(false);

    // ── Auto-approve timer ────────────────────────────────────────────────────
    timeRemaining = signal<number>(AUTO_APPROVE_SECONDS);
    /** True while the countdown is actively ticking. Drives timer visibility in the template. */
    isTimerActive = signal(false);
    private _timerInterval?: ReturnType<typeof setInterval>;

    private threadMessagesService = inject(ThreadMessagesService);

    /** Thread the countdown deadline is persisted against. */
    private get _threadId(): string {
        return this.threadMessagesService.activeThreadId();
    }

    // ── Edit state ───────────────────────────────────────────────────────────
    /** Whether the card is in Markdown edit mode. */
    isEditing = signal(false);

    /** Editable copy of the Markdown content. */
    editableMarkdown = signal('');

    // ── Feedback state ────────────────────────────────────────────────────────
    /** Current text in the feedback textarea. */
    feedbackText = signal('');

    /** Whether a revision request is in flight (loading state). */
    isRevising = signal(false);

    // ── Derived ──────────────────────────────────────────────────────────────
    /** True when the user has changed the Markdown relative to the original enhanced prompt. */
    hasEdits = computed(() =>
        this.editableMarkdown().trim() !== (this.plan()?.markdown ?? '').trim()
    );

    constructor() {
        // The parent has no signal to tell us a revision round-trip has finished —
        // the revised Markdown simply arrives via a new `plan` input value.  React
        // to that: whenever the Markdown actually changes, clear the loading state
        // on the send-feedback button so the user can submit again.
        effect(() => {
            const markdown = this.plan()?.markdown ?? '';
            untracked(() => {
                if (this._lastSeenMarkdown !== markdown) {
                    this._lastSeenMarkdown = markdown;
                    if (this.isRevising()) {
                        this.isRevising.set(false);
                    }
                }
            });
        });
    }

    /** Tracks the last `plan().markdown` value the effect has observed. */
    private _lastSeenMarkdown = '';

    ngOnInit(): void {
        // Resume the deadline this thread was already counting down to, so a
        // browser refresh doesn't hand the user a fresh two minutes.
        const restored = readCountdown(this._threadId);
        if (restored === 0) {
            // The window closed while the user was away — honour it immediately.
            this.onApprove();
            return;
        }
        this._startTimer(restored ?? AUTO_APPROVE_SECONDS);
    }

    ngOnDestroy(): void {
        this._clearTimer();
    }

    /** Begin (or resume) the countdown at `seconds` and persist its deadline. */
    private _startTimer(seconds: number): void {
        this._clearTimer();
        this.timeRemaining.set(seconds);
        this.isTimerActive.set(true);
        writeCountdown(this._threadId, seconds);

        this._timerInterval = setInterval(() => {
            if (this.pauseTimer()) {
                // Hold the display and push the stored deadline out with it, so a
                // refresh while the Save-Prompt dialog is open sees the paused value.
                writeCountdown(this._threadId, this.timeRemaining());
                return;
            }
            const current = this.timeRemaining();
            if (current > 0) {
                this.timeRemaining.set(current - 1);
            } else {
                this.onApprove();
            }
        }, 1000);
    }

    private _clearTimer(): void {
        if (this._timerInterval !== undefined) {
            clearInterval(this._timerInterval);
            this._timerInterval = undefined;
        }
        this.isTimerActive.set(false);
    }

    /** Stop the countdown and forget its deadline — the interrupt is resolved. */
    private _endTimer(): void {
        this._clearTimer();
        clearCountdown(this._threadId);
    }

    // ── Approve / dismiss ─────────────────────────────────────────────────────

    onApprove(): void {
        this._endTimer();
        this.approve.emit();
    }

    onDismiss(): void {
        this._endTimer();
        this.dismiss.emit();
    }

    onSavePrompt(): void {
        this.savePrompt.emit(this.plan()?.markdown ?? '');
    }

    // ── Edit mode ─────────────────────────────────────────────────────────────

    /** Enter edit mode, seeding the editable copy from the current Markdown. */
    enterEditMode(): void {
        // Forget the deadline too: the user has taken over, and a refresh mid-edit
        // should offer a full countdown rather than resume one that has run out.
        this._endTimer();
        this.editableMarkdown.set(this.plan()?.markdown ?? '');
        this.isEditing.set(true);
    }

    cancelEdit(): void {
        this.isEditing.set(false);
        this.editableMarkdown.set('');
        this._startTimer(AUTO_APPROVE_SECONDS);
    }

    /** Emit the edited Markdown and exit edit mode. */
    onEditApprove(): void {
        this.editApprove.emit(this.editableMarkdown());
        this.isEditing.set(false);
    }

    // ── Feedback actions ──────────────────────────────────────────────────────

    /**
     * Handle Enter key on the feedback textarea.
     * Shift+Enter inserts a newline; plain Enter submits.
     */
    onFeedbackKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.submitFeedback();
        }
    }

    /** Emit the feedback text and enter loading state until the parent resolves. */
    submitFeedback(): void {
        const text = this.feedbackText().trim();
        if (!text) return;
        this._endTimer();
        this.isRevising.set(true);
        this.feedbackText.set('');
        this.reviseRequested.emit(text);
    }

    /** Called by the parent once the revise round-trip is complete. */
    clearRevising(): void {
        this.isRevising.set(false);
    }

    // ── Timer helpers ─────────────────────────────────────────────────────────

    /** Format seconds as M:SS for the countdown badge. */
    formatTime(seconds: number): string {
        return formatCountdown(seconds);
    }
}
